<template>
  <div class="portal-login">
    <div class="portal-login__card">
      <div class="portal-login__brand">
        <img class="portal-login__logo" :src="'/assets/images/dauberhq_logo_blue.svg'" alt="DauberHQ">
        <span class="portal-login__badge">Portal</span>
      </div>

      <!-- LOGIN MODE -->
      <template v-if="mode === 'login'">
        <!-- Step 1: credentials -->
        <template v-if="step === 'credentials'">
          <h1 class="portal-login__title">Admin sign-in</h1>
          <p class="portal-login__subtitle">Restricted access. Authorized staff only.</p>

          <form class="portal-login__form" @submit.prevent="submitLogin">
            <div
              v-if="error"
              data-test="login-error"
              class="portal-login__error"
              role="alert"
              aria-live="assertive"
            >{{ error }}</div>

            <label class="portal-login__label" for="pl-email">Email</label>
            <input
              id="pl-email"
              data-test="login-email-input"
              v-model="email"
              type="email"
              class="dhq-input"
              autocomplete="username webauthn"
              required
              :disabled="loading"
            />

            <label class="portal-login__label" for="pl-password">Password</label>
            <input
              id="pl-password"
              data-test="login-password-input"
              v-model="password"
              type="password"
              class="dhq-input"
              autocomplete="current-password"
              required
              :disabled="loading"
            />

            <button
              type="submit"
              data-test="login-submit"
              class="dhq-btn dhq-btn--primary portal-login__submit"
              :disabled="loading"
            >
              {{ loading ? 'Signing in…' : 'Sign in' }}
            </button>
          </form>

          <div class="portal-login__divider"><span>or</span></div>

          <button
            type="button"
            data-test="passkey-button"
            class="dhq-btn dhq-btn--ghost portal-login__passkey"
            :disabled="loading"
            @click="usePasskey"
          >
            <span aria-hidden="true">🔑</span> Sign in with a passkey
          </button>

          <p class="portal-login__links">
            <a data-test="forgot-link" href="/forgot" class="portal-login__link">Forgot password?</a>
          </p>
        </template>

        <!-- Step 2: TOTP -->
        <template v-else-if="step === 'totp'">
          <h1 class="portal-login__title">Two-factor code</h1>
          <p class="portal-login__subtitle">Enter the 6-digit code from your authenticator app.</p>

          <form class="portal-login__form" @submit.prevent="submitTotp">
            <div
              v-if="error"
              data-test="totp-error"
              class="portal-login__error"
              role="alert"
              aria-live="assertive"
            >{{ error }}</div>

            <label class="portal-login__label" for="pl-totp">Authentication code</label>
            <input
              id="pl-totp"
              data-test="totp-code-input"
              v-model="totpCode"
              type="text"
              inputmode="numeric"
              autocomplete="one-time-code"
              class="dhq-input"
              required
              :disabled="loading"
            />

            <button
              type="submit"
              data-test="totp-submit"
              class="dhq-btn dhq-btn--primary portal-login__submit"
              :disabled="loading"
            >
              {{ loading ? 'Verifying…' : 'Verify' }}
            </button>
          </form>
        </template>
      </template>

      <!-- FORGOT MODE -->
      <template v-else-if="mode === 'forgot'">
        <h1 class="portal-login__title">Reset password</h1>
        <p class="portal-login__subtitle">We'll email you a reset link.</p>

        <form class="portal-login__form" @submit.prevent="submitForgot">
          <div v-if="error" class="portal-login__error" role="alert" aria-live="assertive">{{ error }}</div>
          <div v-if="notice" class="portal-login__notice" role="status" aria-live="polite">{{ notice }}</div>

          <label class="portal-login__label" for="pl-forgot-email">Email</label>
          <input
            id="pl-forgot-email"
            data-test="forgot-email-input"
            v-model="email"
            type="email"
            class="dhq-input"
            autocomplete="username"
            required
            :disabled="loading"
          />

          <button
            type="submit"
            data-test="forgot-submit"
            class="dhq-btn dhq-btn--primary portal-login__submit"
            :disabled="loading"
          >
            {{ loading ? 'Sending…' : 'Send reset link' }}
          </button>
        </form>

        <p class="portal-login__links">
          <a href="/login" class="portal-login__link">Back to sign-in</a>
        </p>
      </template>

      <!-- RESET MODE -->
      <template v-else-if="mode === 'reset'">
        <h1 class="portal-login__title">Set a new password</h1>
        <p class="portal-login__subtitle">Choose a strong password (at least 10 characters).</p>

        <form class="portal-login__form" @submit.prevent="submitReset">
          <div
            v-if="error"
            data-test="reset-error"
            class="portal-login__error"
            role="alert"
            aria-live="assertive"
          >{{ error }}</div>
          <div v-if="notice" class="portal-login__notice" role="status" aria-live="polite">{{ notice }}</div>

          <label class="portal-login__label" for="pl-reset-pw">New password</label>
          <input
            id="pl-reset-pw"
            data-test="reset-password-input"
            v-model="password"
            type="password"
            class="dhq-input"
            autocomplete="new-password"
            required
            :disabled="loading"
          />

          <label class="portal-login__label" for="pl-reset-confirm">Confirm password</label>
          <input
            id="pl-reset-confirm"
            data-test="reset-confirm-input"
            v-model="confirm"
            type="password"
            class="dhq-input"
            autocomplete="new-password"
            required
            :disabled="loading"
          />

          <button
            type="submit"
            data-test="reset-submit"
            class="dhq-btn dhq-btn--primary portal-login__submit"
            :disabled="loading"
          >
            {{ loading ? 'Saving…' : 'Save new password' }}
          </button>
        </form>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';

// base64url <-> ArrayBuffer helpers for WebAuthn
function b64uToBuf(b64u) {
  const b64 = b64u.replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4 ? '='.repeat(4 - (b64.length % 4)) : '';
  const bin = atob(b64 + pad);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
}
function bufToB64u(buf) {
  const bytes = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

const init = (typeof window !== 'undefined' && window.PORTAL_AUTH_INIT) || {};
const mode = ref(init.mode || 'login');
const csrfToken = init.csrf_token || '';
const resetToken = init.token || '';

const step = ref('credentials'); // login flow: 'credentials' | 'totp'
const email = ref('');
const password = ref('');
const confirm = ref('');
const totpCode = ref('');
const error = ref('');
const notice = ref('');
const loading = ref(false);

// Form-encoded POST helper. The portal auth endpoints read $_POST (PHP does
// NOT populate $_POST from a JSON body), so bodies must be url-encoded.
function postForm(url, payload) {
  const body = new URLSearchParams();
  for (const [k, v] of Object.entries(payload)) body.append(k, v);
  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
}

// Resolve a response into its JSON payload regardless of HTTP status — the API
// returns { ok, error } envelopes even on 4xx, and the spec rejects creds with
// an ok:false fetch whose JSON carries the error message.
async function resolveJson(res) {
  try {
    return (await res.json()) || {};
  } catch (e) {
    return {};
  }
}

async function submitLogin() {
  if (loading.value) return;
  loading.value = true;
  error.value = '';
  try {
    const res = await postForm('/api/auth/login', {
      email: email.value,
      password: password.value,
      csrf_token: csrfToken,
    });
    const data = await resolveJson(res);
    if (data.ok) {
      if (data.step === 'totp') {
        step.value = 'totp';
      } else {
        window.location.href = data.redirect || '/';
      }
    } else {
      error.value = data.error || 'Sign-in failed.';
    }
  } catch (e) {
    error.value = 'Network error. Please try again.';
  } finally {
    loading.value = false;
  }
}

async function submitTotp() {
  if (loading.value) return;
  loading.value = true;
  error.value = '';
  try {
    const res = await postForm('/api/auth/totp-verify', {
      code: totpCode.value,
      csrf_token: csrfToken,
    });
    const data = await resolveJson(res);
    if (data.ok) {
      window.location.href = data.redirect || '/';
    } else {
      error.value = data.error || 'Invalid code.';
    }
  } catch (e) {
    error.value = 'Network error. Please try again.';
  } finally {
    loading.value = false;
  }
}

async function submitForgot() {
  if (loading.value) return;
  loading.value = true;
  error.value = '';
  notice.value = '';
  try {
    const res = await postForm('/api/auth/forgot', {
      email: email.value,
      csrf_token: csrfToken,
    });
    const data = await resolveJson(res);
    if (data.ok) {
      notice.value = "If an account exists, we've sent a reset link.";
    } else {
      error.value = data.error || 'Could not send reset link.';
    }
  } catch (e) {
    error.value = 'Network error. Please try again.';
  } finally {
    loading.value = false;
  }
}

async function submitReset() {
  error.value = '';
  notice.value = '';
  // Client-side guard — must NOT fire a request on mismatch / too-short.
  if (password.value !== confirm.value) {
    error.value = 'Passwords do not match.';
    return;
  }
  if (password.value.length < 10) {
    error.value = 'Password must be at least 10 characters.';
    return;
  }
  if (loading.value) return;
  loading.value = true;
  try {
    const res = await postForm('/api/auth/reset', {
      token: resetToken,
      password: password.value,
      csrf_token: csrfToken,
    });
    const data = await resolveJson(res);
    if (data.ok) {
      window.location.href = data.redirect || '/login';
    } else {
      error.value = data.error || 'Could not reset password.';
    }
  } catch (e) {
    error.value = 'Network error. Please try again.';
  } finally {
    loading.value = false;
  }
}

async function usePasskey() {
  if (loading.value) return;
  loading.value = true;
  error.value = '';
  try {
    const challengeRes = await fetch('/api/auth/passkey-challenge', { method: 'GET' });
    const challenge = await resolveJson(challengeRes);
    if (!challenge.ok) {
      error.value = challenge.error || 'Passkey sign-in unavailable.';
      return;
    }
    // Guard: WebAuthn may be absent (jsdom / unsupported browsers). The spec
    // only asserts the challenge POST fired on click, so bail gracefully here.
    if (!(typeof navigator !== 'undefined' && navigator.credentials && navigator.credentials.get)) {
      error.value = 'Passkeys are not supported in this browser.';
      return;
    }
    const publicKey = { ...(challenge.options || challenge.publicKey || {}) };
    publicKey.challenge = b64uToBuf(publicKey.challenge);
    if (publicKey.allowCredentials) {
      publicKey.allowCredentials = publicKey.allowCredentials.map((c) => ({
        ...c,
        id: b64uToBuf(c.id),
      }));
    }
    const cred = await navigator.credentials.get({ publicKey });
    if (!cred) {
      error.value = 'No passkey selected.';
      return;
    }
    const credential = {
      id: cred.id,
      rawId: bufToB64u(cred.rawId),
      type: cred.type,
      response: {
        authenticatorData: bufToB64u(cred.response.authenticatorData),
        clientDataJSON: bufToB64u(cred.response.clientDataJSON),
        signature: bufToB64u(cred.response.signature),
        userHandle: cred.response.userHandle ? bufToB64u(cred.response.userHandle) : null,
      },
    };
    const verifyRes = await postForm('/api/auth/passkey-login', {
      credential: JSON.stringify(credential),
      csrf_token: csrfToken,
    });
    const result = await resolveJson(verifyRes);
    if (result.ok) {
      window.location.href = result.redirect || '/';
    } else {
      error.value = result.error || 'Passkey sign-in failed.';
    }
  } catch (e) {
    error.value = 'Passkey sign-in was cancelled.';
  } finally {
    loading.value = false;
  }
}
</script>
